import styled, { keyframes } from 'styled-components';
import { ScaleVisualization } from '~src/interfaces/scale.interface';

import { OrderedColorSets, orderedColorsSets, orderedColorsSetsReversed } from '~src/styles/colors';
import { media } from '~src/styles/media-templates';

const getColor = ({ $reversed, $scaleLength, $colorNumber, $type }: ColorBoxProps) => {
  if ($type === ScaleVisualization.McqOptions) {
    return orderedColorsSets[3][1];
  }

  const colorsSet: OrderedColorSets = $reversed ? orderedColorsSetsReversed : orderedColorsSets;

  return colorsSet[$scaleLength][$colorNumber];
};

const particleD = 0.5; // size of the bubbles
const particleR = 0.55 * particleD;

export const calcShadowList = (k: number, type: string, partD: number, partR: number, color?: string) => {
  let bubbleD;
  let bubbleR;
  let nParticles;
  let stretchV;
  switch (type) {
    case 'nps': {
      bubbleD = 8;
      bubbleR = 0.38 * bubbleD;
      nParticles = 2;
      stretchV = 0;
      break;
    }
    case 'stars': {
      bubbleD = 5;
      bubbleR = 0.5 * bubbleD;
      nParticles = 0;
      stretchV = 0;
      break;
    }
    default: {
      bubbleD = 9;
      bubbleR = 0.5 * bubbleD;
      nParticles = 2;
      stretchV = 0.6;
    }
  }
  let shadowList = '';
  const nGroups = 10;
  const groupBaseAngle = 360 / nGroups;
  const groupDistrR = (1 + k * 0.25) * bubbleR;
  const particleBaseAngle = 360 / (nParticles || 1);
  const particleOffAngle = 60; // offset angle from radius
  const spreadR = -k * partR;
  for (let i = 0; i <= nGroups; i++) {
    const groupCurrAngle = i * groupBaseAngle - 90;
    const xg = groupDistrR * Math.cos(groupCurrAngle);
    const yg = groupDistrR * Math.sin(groupCurrAngle - stretchV);
    for (let j = 0; j <= nParticles; j++) {
      const particleCurrAngle = groupCurrAngle + particleOffAngle + j * particleBaseAngle;
      const xs = xg + partD * Math.cos(particleCurrAngle);
      const ys = yg + partD * Math.sin(particleCurrAngle);
      shadowList = `${shadowList}${shadowList.length > 0 ? ',' : ''} ${xs}rem ${ys}rem 0 ${spreadR}rem ${
        color || orderedColorsSets[10][(j + i) % 10]
      }`;
    }
  }
  return shadowList;
};

const bordergrow = keyframes`
  0% {
    display: block;
    border: solid 0 inherit;
  }
  10% {
    border-width: 0;
    transform: scale(0.8);
  }
  30% {
    border-color: white;
    transform: scale(1.2);
  }
  40% {
    border-width: 1px;
    transform: scale(1);
  }
`;

const bubbleGrow = (type: string) =>
  keyframes`
  0% {
    opacity: 0;
    display: block;
  } 25% {
    opacity: 0;
    box-shadow: ${calcShadowList(0, type, particleD, particleR)};
  }
	100% {
		opacity: 1;
    box-shadow: ${calcShadowList(1, type, particleD, particleR)};
	}
`;

const labelgrow = keyframes`
   0% {
    transform: scale(1);
   }
   10% {
    transform: scale(0.9);
   }
   60% {
     transform: scale(1);
   }
`;

interface ColorBoxProps {
  $type: ScaleVisualization;
  $scaleSelected?: boolean;
  $selected: boolean;
  $reversed: boolean;
  $scaleLength: number;
  $colorNumber: number;
  $withHover: boolean;
  $animated?: boolean;
  $isMinChar40: boolean;
  $showCheckboxOptions?: boolean;
}

export const ColoredBox = styled.div<ColorBoxProps>`
  width: ${({ $type, $isMinChar40, $showCheckboxOptions }) =>
    $type === ScaleVisualization.Nps ? '44px' : $isMinChar40 ? ($showCheckboxOptions ? '165px' : '140px') : ($showCheckboxOptions ? '225px' : '200px')};
  height: ${({ $isMinChar40, $showCheckboxOptions }) => ($isMinChar40 ? ($showCheckboxOptions ? '63px' : '44px') : '63px')};
  display: flex;
  position: relative;
  z-index: 10;
  color: ${({ theme, $scaleSelected, $selected }) =>
    $scaleSelected && !$selected ? theme.white.a5 : theme.white.a10};
  background-color: ${(props) =>
    props.$scaleSelected && !props.$selected ? 'rgba(0, 0, 0, 0)' : getColor(props)};
  font-size: 12px;
  text-align: center;
  line-height: 17px;
  font-weight: 500;
  align-items: center;
  justify-content: ${({ $showCheckboxOptions }) => ($showCheckboxOptions ? 'flex-start' : 'center')};
  padding: ${({ $showCheckboxOptions }) => ($showCheckboxOptions ? '5px 5px 5px 12px ' : '5px')};
  border-radius: 5px;
  transform: scale(1);
  border: ${({ $scaleSelected, $selected }) =>
    $scaleSelected && !$selected ? '1px solid rgba(255, 255, 255, 0.5)' : 'none'};
  cursor: ${({ $withHover }) => ($withHover ? 'pointer' : 'auto')};
  animation-duration: 1s;
  animation-timing-function: ease-out;
  animation-name: ${({ $selected, $animated }) => ($selected && $animated ? labelgrow : 'none')};
  opacity: ${({ $selected, $animated }) => ($selected && $animated ? '1 !important' : 'inherit')};

  &:focus {
    outline: none;
  }
  &:hover {
    background-color: ${getColor};
    border: none;
    color: ${({ theme }) => theme.white.a10};
    opacity: 0.7;
  }
  &::before,
  &::after {
    animation: ${({ $selected, $animated }) => ($selected && $animated ? 'inherit' : 'none')};
    animation-timing-function: ${({ $selected, $animated }) =>
      $selected && $animated ? 'ease-out' : 'none'};
  }
  /* Border */
  &::before {
    display: ${({ $selected }) => ($selected ? 'block' : 'none')};
    animation-name: ${({ $selected }) => ($selected ? bordergrow : 'none')};
    position: absolute;
    z-index: -1;
    border-radius: 8px;
    top: -3px;
    left: -3px;
    content: '';
    box-sizing: border-box;
    border: ${(props) => (props.$selected && props.$animated ? `solid 1px ${getColor(props)}` : 'none')};
    width: ${({ $type, $isMinChar40 }) => ($type === ScaleVisualization.Nps ? '50px' : $isMinChar40 ? '146px' : '206px')};
    height: ${({ $isMinChar40 }) => ($isMinChar40 ? '50px' : '66px')};
    &:focus {
      outline: none;
    }
  }
  /* Particles */
  &::after {
    position: absolute;
    animation-name: ${({ $type, $selected, $animated }) =>
      $selected && $animated ? bubbleGrow($type) : 'none'};
    top: 50%;
    left: 50%;
    will-change: opacity, box-shadow;
    border-radius: 50%;
    display: ${({ $selected }) => ($selected ? 'block' : 'none')};
    transform: scale(1) rotate(45deg);
    content: '';
    z-index: 33;
    margin: -${particleR}rem;
    width: ${particleD}rem;
    height: ${particleD}rem;
    box-shadow: ${({ $type }) => calcShadowList(1, $type, particleD, particleR)};
    &:focus {
      outline: none;
    }
  }

  ${media.phone`
    width: 100% !important;
      &::before {
        width: calc(100% + 6px) !important;
      }
  `}
  &.vertical {
    width: 100% !important;
    &::before {
      width: calc(100% + 6px) !important;
    }
  }

  span {
    max-height: 100%;
    padding-left: ${({ $showCheckboxOptions }) => ($showCheckboxOptions ? '0.8rem' : '0rem')};
    text-align: ${({ $showCheckboxOptions }) => ($showCheckboxOptions ? 'left' : 'center')};
  }
`;
